import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, X, CheckCircle2, Bot, User as UserIcon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TID = {
  fab: "copilot-fab",
  drawer: "copilot-drawer",
  close: "copilot-close",
  input: "copilot-input",
  send: "copilot-send",
  confirm: (a) => `copilot-confirm-${a}`,
  cancel: "copilot-cancel-action",
};

const SUGGESTIONS = [
  "marca corte pro João amanhã 15h",
  "quais horários livres hoje?",
  "cancela o próximo agendamento da Maria",
];

const ACTION_LABEL = {
  create_appointment: "Criar agendamento",
  reschedule_appointment: "Remarcar",
  cancel_appointment: "Cancelar agendamento",
  find_slots: "Buscar horários",
  list_client_appointments: "Listar agendamentos",
  answer: "Responder",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

/**
 * Copiloto IA — chat flutuante no admin.
 * Fluxo:
 *  1. Admin digita  → POST /tenant/copilot/interpret
 *  2. Backend retorna { action, params, resolved, confirmation_text }
 *  3. Admin confirma → POST /tenant/copilot/execute
 */
export default function CopilotDrawer() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', text?, proposal? }
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const submit = async (text) => {
    const q = (text ?? msg).trim();
    if (!q || busy) return;
    setMsg("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/copilot/interpret", { message: q, history: [] });
      setMessages((m) => [...m, { role: "assistant", proposal: data }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e) || "Falhou." }]);
    } finally {
      setBusy(false);
    }
  };

  const execute = async (proposal) => {
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/copilot/execute", {
        action: proposal.action, params: proposal.params,
      });
      const summary = buildExecSummary(proposal, data);
      setMessages((m) => [...m, { role: "assistant", text: summary, done: true }]);
      toast.success("Feito!");
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e) || "Falhou ao executar." }]);
      toast.error("Não consegui executar essa ação.");
    } finally {
      setBusy(false);
    }
  };

  const clearChat = () => setMessages([]);

  return (
    <>
      {/* FAB */}
      <button
        data-testid={TID.fab}
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 rounded-full shadow-xl px-4 py-3 flex items-center gap-2 hover:scale-105 transition-transform text-white"
        style={{ background: "hsl(var(--tenant-primary, 258 90% 66%))" }}
        aria-label="Abrir Copiloto"
      >
        <Sparkles className="w-4 h-4" />
        <span className="font-medium hidden sm:inline">Copiloto</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid={TID.drawer}
        >
          <Card
            className="w-full sm:max-w-md h-full rounded-none sm:rounded-l-2xl flex flex-col border-l border-border bg-background"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-border flex items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                  style={{ background: "hsl(var(--tenant-primary, 258 90% 66%))" }}>
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-display font-semibold">Copiloto</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sua agenda em linguagem natural</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <Button size="icon" variant="ghost" onClick={clearChat} title="Novo chat">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={() => setOpen(false)} data-testid={TID.close}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Mensagens */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-sm text-muted-foreground space-y-4 py-6">
                  <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center bg-muted">
                    <Bot className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <p>Peça em português. Eu monto a ação, você confirma.</p>
                  <div className="space-y-2 max-w-xs mx-auto">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => submit(s)}
                        className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                        data-testid={`copilot-suggestion-${s.slice(0, 12).replace(/\s+/g, '-')}`}
                      >
                        “{s}”
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <MessageBubble key={i} message={m} onExecute={execute} busy={busy} />
              ))}
              {busy && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> pensando...
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); submit(); }}
              className="p-3 border-t border-border flex gap-2 shrink-0"
            >
              <Input
                data-testid={TID.input}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Ex.: agenda um corte pro Bruno sexta 10h"
                disabled={busy}
              />
              <Button type="submit" data-testid={TID.send} disabled={busy || !msg.trim()}
                style={{ background: "hsl(var(--tenant-primary, 258 90% 66%))", color: "white" }}>
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}

function MessageBubble({ message, onExecute, busy }) {
  if (message.role === "user") {
    return (
      <div className="flex items-start gap-2 justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2 text-sm text-white"
          style={{ background: "hsl(var(--tenant-primary, 258 90% 66%))" }}>
          {message.text}
        </div>
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
          <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white"
        style={{ background: "hsl(var(--tenant-primary, 258 90% 66%))" }}>
        <Sparkles className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        {message.text && (
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm px-3 py-2 text-sm bg-muted">
            {message.done && <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-600" />}
            {message.text}
          </div>
        )}
        {message.proposal && (
          <ProposalCard proposal={message.proposal} onExecute={onExecute} busy={busy} />
        )}
      </div>
    </div>
  );
}

function ProposalCard({ proposal, onExecute, busy }) {
  const { action, params, resolved, confirmation_text, needs } = proposal;
  const canExecute =
    (action === "create_appointment" && params.client_id && params.service_id && params.start_at) ||
    (action === "reschedule_appointment" && params.client_id && params.start_at) ||
    (action === "cancel_appointment" && params.client_id) ||
    (action === "find_slots" && params.service_id && params.date) ||
    (action === "list_client_appointments" && params.client_id);
  const missing = [];
  if (!params.client_id && (needs || []).includes("client_not_found"))
    missing.push(`Cliente “${params.client_name}” não encontrado.`);
  if (!params.service_id && (needs || []).includes("service_not_found"))
    missing.push(`Serviço “${params.service_name}” não encontrado.`);
  if (action === "answer") missing.push(confirmation_text || "Preciso de mais detalhes.");

  return (
    <Card className="p-3 rounded-xl border-border bg-background max-w-[95%]" data-testid={`copilot-proposal-${action}`}>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="outline" className="text-[10px]">{ACTION_LABEL[action] || action}</Badge>
        {resolved?.client && <span className="text-xs text-muted-foreground">→ {resolved.client.name}</span>}
      </div>
      {confirmation_text && (
        <div className="text-sm mb-2">{confirmation_text}</div>
      )}
      <div className="space-y-1 text-xs text-muted-foreground">
        {resolved?.service && <div>Serviço: <strong className="text-foreground">{resolved.service.name}</strong>{resolved.service.duration_minutes ? ` · ${resolved.service.duration_minutes}min` : ""}</div>}
        {resolved?.resource && <div>Profissional: <strong className="text-foreground">{resolved.resource.name}</strong></div>}
        {params.start_at && <div>Quando: <strong className="text-foreground">{formatDate(params.start_at)}</strong></div>}
        {params.date && !params.start_at && <div>Data: <strong className="text-foreground">{params.date}</strong></div>}
      </div>
      {missing.length > 0 && (
        <div className="mt-2 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded p-2">
          {missing.map((m, i) => <div key={i}>• {m}</div>)}
        </div>
      )}
      {canExecute && missing.length === 0 && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={() => onExecute(proposal)} disabled={busy}
            data-testid={TID.confirm(action)}
            style={{ background: "hsl(var(--tenant-primary, 258 90% 66%))", color: "white" }}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirmar
          </Button>
        </div>
      )}
    </Card>
  );
}

function buildExecSummary(proposal, data) {
  const a = proposal.action;
  if (a === "create_appointment") {
    const r = data?.result || {};
    return `Agendado: ${r.client_name || ""} — ${r.service_name || ""} em ${formatDate(r.start_at)}.`;
  }
  if (a === "reschedule_appointment") {
    const r = data?.result || {};
    return `Remarcado para ${formatDate(r.start_at)}.`;
  }
  if (a === "cancel_appointment") return "Agendamento cancelado.";
  if (a === "find_slots") {
    const slots = (data?.result?.slots || []).filter((s) => s.available);
    if (slots.length === 0) return "Nenhum horário disponível nesse dia.";
    const preview = slots.slice(0, 6).map((s) => s.label).join(", ");
    return `${slots.length} horários livres: ${preview}${slots.length > 6 ? "..." : ""}`;
  }
  if (a === "list_client_appointments") {
    const items = data?.items || [];
    if (items.length === 0) return "Sem agendamentos.";
    return items.slice(0, 5).map((i) => `• ${formatDate(i.start_at)} — ${i.service_name} (${i.status})`).join("\n");
  }
  return "Feito.";
}
